import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { addDays, format } from "date-fns";
import { useParcelamentoNormal } from "./useParcelamentoNormal";
import { useParcelamentoEspecial } from "./useParcelamentoEspecial";
import { useIntervalo } from "./useIntervalo";
import { usePrazoMedio } from "./usePrazoMedio";
import { useCondicoes } from "./useCondicoes";
import { getClientData } from "../../services/auth";
import {
  ParcelamentoTotals,
  CondicaoParcelamento,
  CondicaoEspecial,
  ParcelamentoNormalResponse,
} from "../../types/Parcelamento";

interface ParcelamentoState {
  // Estado local (seleções do usuário)
  parcela: string;
  intervalo: string;
  prazo: string;
  codigoParcelamentoEspecial: string;
  qtdDias: string;
  useEspecialConditions: boolean;

  // Dados das queries
  parcelas: (string | number)[];
  intervalos: (string | number)[];
  prazoMedio: (string | number)[];
  condicoesParcelamento: CondicaoParcelamento[] | string;
  condicoesParcelamentoEspecial: CondicaoEspecial[];
  minimum: number;
  prazoMaximo?: string | undefined;
  codigoParcelamento?: string | undefined;
  isLoading: boolean;
}

export const useParcelamento = (totals: ParcelamentoTotals | null) => {
  // Calcula o mínimo
  // Esta lógica será implementada quando tivermos acesso ao parcelamentoGenericos
  // Por enquanto, usa valores padrão baseados na empresa
  // Não usa useMemo pois getClientData() pode mudar e não há dependências estáveis
  const clientData = getClientData();
  const minimum = clientData?.cd_empresa === 105 ? 500 : 600;

  // Reset quando os totais mudam significativamente (quando itens são adicionados/removidos)
  const totalsKey = totals
    ? `${totals.totalLiquido}-${totals.totalMedicamentos}-${totals.totalPerfumaria}`
    : "";

  // Estado local
  const [parcela, setParcela] = useState<string>("");
  const [intervalo, setIntervalo] = useState<string>("");
  const [prazo, setPrazo] = useState<string>("");
  const [codigoParcelamentoEspecial, setCodigoParcelamentoEspecial] =
    useState<string>("");
  const [qtdDias, setQtdDias] = useState<string>("");
  const [useEspecialConditions, setUseEspecialConditions] =
    useState<boolean>(false);
  const [condicoesParcelamentoCalculadas, setCondicoesParcelamentoCalculadas] =
    useState<CondicaoParcelamento[]>([]);

  // Rastreia se já inicializamos useEspecialConditions baseado nas condições especiais
  const hasInitializedEspecialConditions = useRef<boolean>(false);

  // Query de parcelamento normal
  const parcelamentoNormalParams = totals
    ? {
        totalLiquido: totals.totalLiquido,
        totalMedicamentos: totals.totalMedicamentos,
        totalPerfumaria: totals.totalPerfumaria,
        minimum,
      }
    : null;

  const { data: parcelamentoNormalData, isLoading: isLoadingNormal } =
    useParcelamentoNormal(
      parcelamentoNormalParams || undefined,
      totals !== null && totals.totalLiquido >= minimum
    );

  // Query de parcelamento especial
  const parcelamentoEspecialParams = totals
    ? {
        valorPedido: totals.totalLiquido,
      }
    : null;

  const { data: parcelamentoEspecialData, isLoading: isLoadingEspecial } =
    useParcelamentoEspecial(parcelamentoEspecialParams, totals !== null);

  // Query de intervalo (depende de parcela)
  const intervaloParams =
    totals && parcela
      ? {
          totalLiquido: totals.totalLiquido,
          totalMedicamentos: totals.totalMedicamentos,
          totalPerfumaria: totals.totalPerfumaria,
          parcela,
        }
      : null;

  const { data: intervaloData, isLoading: isLoadingIntervalo } = useIntervalo(
    intervaloParams,
    !!parcela
  );

  // Query de prazo médio (depende de parcela e intervalo)
  const prazoMedioParams =
    totals && parcela && intervalo
      ? {
          totalLiquido: totals.totalLiquido,
          totalMedicamentos: totals.totalMedicamentos,
          totalPerfumaria: totals.totalPerfumaria,
          parcela,
          intervalo,
        }
      : null;

  const { data: prazoMedioData, isLoading: isLoadingPrazo } = usePrazoMedio(
    prazoMedioParams,
    !!parcela && !!intervalo
  );

  // Query de condições (depende de parcela, intervalo e prazo)
  const condicoesParams =
    totals && parcela && intervalo && prazo
      ? {
          totalLiquido: totals.totalLiquido,
          totalMedicamentos: totals.totalMedicamentos,
          totalPerfumaria: totals.totalPerfumaria,
          parcela,
          intervalo,
          prazo,
        }
      : null;

  const { data: condicoesData, isLoading: isLoadingCondicoes } = useCondicoes(
    condicoesParams,
    !!parcela && !!intervalo && !!prazo
  );

  // Estado de loading combinado
  const isLoading =
    isLoadingNormal ||
    isLoadingEspecial ||
    isLoadingIntervalo ||
    isLoadingPrazo ||
    isLoadingCondicoes;

  // Calcula parcelas especiais quando necessário
  // Usa valores primitivos nas dependências para evitar loops infinitos
  const totalLiquido = totals?.totalLiquido ?? 0;
  useEffect(() => {
    if (useEspecialConditions && totals) {
      if (qtdDias) {
        const nrDias = Number(qtdDias);
        setCondicoesParcelamentoCalculadas([
          {
            parcela: 1,
            valor: totals.totalLiquido.toFixed(2).replace(".", ","),
            vencimento: format(addDays(new Date(), nrDias), "dd/MM/yyyy"),
          },
        ]);
      } else if (
        codigoParcelamentoEspecial &&
        parcelamentoEspecialData?.condicoesParcelamento
      ) {
        const condicao = parcelamentoEspecialData.condicoesParcelamento.find(
          c => c.cdParcela.toString() === codigoParcelamentoEspecial.toString()
        );

        if (condicao) {
          const nrParcelas = condicao.dias.length;
          const parcelas = totals.totalLiquido / nrParcelas;

          let resto = totals.totalLiquido % nrParcelas;
          if (resto > 0) {
            const rr = Number((resto / nrParcelas).toFixed(2)) * nrParcelas;
            resto -= rr;
          }

          const condicoesParcela: CondicaoParcelamento[] = [];
          for (let i = 0; i < nrParcelas; i += 1) {
            const diasValue = condicao.dias[i];
            if (diasValue !== undefined) {
              condicoesParcela.push({
                parcela: i + 1,
                valor: (parcelas + resto).toFixed(2).replace(".", ","),
                vencimento: format(
                  addDays(new Date(), diasValue),
                  "dd/MM/yyyy"
                ),
              });
            }
          }
          setCondicoesParcelamentoCalculadas(condicoesParcela);
        }
      } else {
        setCondicoesParcelamentoCalculadas([]);
      }
    } else {
      setCondicoesParcelamentoCalculadas([]);
    }
  }, [
    useEspecialConditions,
    qtdDias,
    codigoParcelamentoEspecial,
    parcelamentoEspecialData?.condicoesParcelamento,
    totalLiquido,
  ]);

  // Inicializa useEspecialConditions quando há condições especiais disponíveis pela primeira vez
  useEffect(() => {
    const hasEspecialConditions =
      parcelamentoEspecialData?.condicoesParcelamento &&
      parcelamentoEspecialData.condicoesParcelamento.length > 0;

    // Só atualiza automaticamente na primeira vez que as condições especiais chegarem
    if (hasEspecialConditions && !hasInitializedEspecialConditions.current) {
      setUseEspecialConditions(true);
      hasInitializedEspecialConditions.current = true;
    } else if (
      !hasEspecialConditions &&
      hasInitializedEspecialConditions.current
    ) {
      // Se as condições especiais desaparecerem, desabilita e reseta o flag
      setUseEspecialConditions(false);
      hasInitializedEspecialConditions.current = false;
    }
  }, [parcelamentoEspecialData?.condicoesParcelamento]);

  // Reset quando os totais mudam (itens adicionados/removidos)
  const prevTotalsKeyRef = useRef<string>("");
  useEffect(() => {
    if (totalsKey && totalsKey !== prevTotalsKeyRef.current) {
      prevTotalsKeyRef.current = totalsKey;
      setParcela("");
      setIntervalo("");
      setPrazo("");
      setCodigoParcelamentoEspecial("");
      setQtdDias("");
      setCondicoesParcelamentoCalculadas([]);
      // Reseta o flag de inicialização para permitir nova inicialização automática
      hasInitializedEspecialConditions.current = false;
    }
  }, [totalsKey]);

  const prevParcelaRef = useRef<string>("");
  useEffect(() => {
    if (prevParcelaRef.current !== "" && parcela !== prevParcelaRef.current) {
      setIntervalo("");
      setPrazo("");
    }
    prevParcelaRef.current = parcela;
  }, [parcela]);

  const prevIntervaloRef = useRef<string>("");
  useEffect(() => {
    if (
      prevIntervaloRef.current !== "" &&
      intervalo !== prevIntervaloRef.current
    ) {
      setPrazo("");
    }
    prevIntervaloRef.current = intervalo;
  }, [intervalo]);

  // Funções de reset
  const resetParcelamento = () => {
    setParcela("");
    setIntervalo("");
    setPrazo("");
    setCodigoParcelamentoEspecial("");
    setQtdDias("");
    setUseEspecialConditions(false);
    setCondicoesParcelamentoCalculadas([]);
  };

  const resetParcelamentoConditions = () => {
    setParcela("");
    setIntervalo("");
    setPrazo("");
    setCodigoParcelamentoEspecial("");
    setQtdDias("");
    setCondicoesParcelamentoCalculadas([]);
  };

  // Determina quais condições de parcelamento mostrar
  const condicoesParcelamento = useMemo(() => {
    if (useEspecialConditions) {
      return condicoesParcelamentoCalculadas;
    }
    return condicoesData?.condicoesParcelamento || [];
  }, [useEspecialConditions, condicoesParcelamentoCalculadas, condicoesData]);

  // Extrai dados das queries (não precisa de useMemo para extrações simples)
  const parcelas =
    (parcelamentoNormalData as ParcelamentoNormalResponse | undefined)
      ?.parcelas || [];
  const intervalos = intervaloData?.intervalos || [];
  const prazoMedio = prazoMedioData?.prazoMedio || [];
  const condicoesParcelamentoEspecial =
    parcelamentoEspecialData?.condicoesParcelamento || [];

  // Memoiza o objeto state completo para evitar recriações desnecessárias
  const state = useMemo<ParcelamentoState>(
    () => ({
      parcela,
      intervalo,
      prazo,
      codigoParcelamentoEspecial,
      qtdDias,
      useEspecialConditions,
      parcelas,
      intervalos,
      prazoMedio,
      condicoesParcelamento,
      condicoesParcelamentoEspecial,
      minimum,
      prazoMaximo: prazoMedioData?.callback?.post?.PZ_MAXIMO ?? undefined,
      codigoParcelamento: condicoesData?.cd_parcelamento ?? undefined,
      isLoading,
    }),
    [
      parcela,
      intervalo,
      prazo,
      codigoParcelamentoEspecial,
      qtdDias,
      useEspecialConditions,
      parcelas,
      intervalos,
      prazoMedio,
      condicoesParcelamento,
      condicoesParcelamentoEspecial,
      minimum,
      prazoMedioData?.callback?.post?.PZ_MAXIMO,
      condicoesData?.cd_parcelamento,
      isLoading,
    ]
  );

  // Usa useCallback para memoizar função (mais apropriado que useMemo)
  const setParcelamentoEspecialFn = useCallback(
    (qtdDias: string, codigo: string) => {
      setQtdDias(qtdDias);
      setCodigoParcelamentoEspecial(codigo);
    },
    []
  );

  return {
    ...state,
    setParcela,
    setIntervalo,
    setPrazo,
    setParcelamentoEspecial: setParcelamentoEspecialFn,
    setUseEspecialConditions,
    resetParcelamento,
    resetParcelamentoConditions,
  };
};
